/** The full SKU DataGrid — the L2 behind "View all SKUs" on the waste chart.
 *
 *  It used to sit on the board itself; per the dashboard design rules a full
 *  entity list is an L2 view, so it now lives one click deeper and the board
 *  keeps only the aggregate bar chart. Columns: purchased, expiring, suspended,
 *  locked-out, assigned, unused, at-risk, utilisation, and — only when per-SKU rates have been
 *  configured — estimated cost. Microsoft Graph carries no price data, so an
 *  always-on cost column would be a column of invented numbers.
 *
 *  Purchased and expiring are deliberately two columns rather than one figure
 *  with a footnote. They answer different questions ("what do we pay for" and
 *  "what runs out"), and the version of this grid that showed them as one
 *  number is what put 3,693 seats of imaginary waste on the board.
 *
 *  Suspended and locked-out are here for the same reason and are the last two
 *  `prepaidUnits` buckets to reach the UI: both are capacity on a cancelled
 *  subscription — suspended can be reactivated, locked-out cannot — so both
 *  are their own column, never summed with each other or with purchased.
 */

import { Badge, DataGrid, EmptyState, Meter } from "@norican/react";

import type { LicenseSkuRow } from "@/lib/api";

import {
  EXCLUSION_LABEL,
  formatMoney,
  formatPct,
  formatSeats,
  SEAT_LABEL,
  utilisationTone,
} from "./licence-format";

interface SkuWasteGridProps {
  rows: LicenseSkuRow[];
  currency: string;
  pricesConfigured: boolean;
}

export function SkuWasteGrid({ rows, currency, pricesConfigured }: SkuWasteGridProps) {
  if (rows.length === 0) {
    return (
      <EmptyState
        size="sm"
        title="No SKUs to list"
        description="The tenant reports no subscribed SKUs."
      />
    );
  }

  return (
    <div className="h-[28rem]">
      {/* DataGrid's scroller is `max-height: 100%`, so pinned columns and the
          sticky header only get a scroll region inside a DEFINITE height.
          Without one they latch onto the page viewport and drift while the
          page scrolls. Every DataGrid story wraps the grid this way. */}
      <DataGrid<LicenseSkuRow>
        data={rows}
        getRowId={(sku) => sku.sku_id || sku.sku_part_number}
        getRowSearchText={(sku) => `${sku.name} ${sku.sku_part_number}`}
        density="compact"
        aria-label="All subscribed SKUs with purchased, expiring, suspended, locked-out, assigned, unused and at-risk seat counts"
        columns={[
          {
            id: "name",
            header: "SKU",
            accessor: (sku) => sku.name,
            sortable: true,
            pinned: "left",
            cell: (sku) => (
              <div className="flex flex-col">
                <span className="text-fg font-medium">{sku.name}</span>
                <span className="text-fg-muted text-xs">{sku.sku_part_number}</span>
              </div>
            ),
          },
          {
            id: "purchased",
            header: SEAT_LABEL.purchased,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.purchased,
            cell: (sku) => (
              <span className="text-fg tabular-nums">{formatSeats(sku.purchased)}</span>
            ),
          },
          {
            id: "grace",
            header: SEAT_LABEL.grace,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.grace,
            cell: (sku) =>
              sku.grace > 0 ? (
                <span className="text-warning-fg tabular-nums">{formatSeats(sku.grace)}</span>
              ) : (
                <span className="text-fg-subtle tabular-nums">0</span>
              ),
          },
          {
            id: "suspended",
            header: SEAT_LABEL.suspended,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.suspended,
            cell: (sku) =>
              sku.suspended > 0 ? (
                <span className="text-warning-fg tabular-nums">{formatSeats(sku.suspended)}</span>
              ) : (
                <span className="text-fg-subtle tabular-nums">0</span>
              ),
          },
          {
            id: "locked_out",
            header: SEAT_LABEL.lockedOut,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.locked_out,
            cell: (sku) =>
              sku.locked_out > 0 ? (
                <span className="text-danger-fg tabular-nums">{formatSeats(sku.locked_out)}</span>
              ) : (
                <span className="text-fg-subtle tabular-nums">0</span>
              ),
          },
          {
            id: "assigned",
            header: SEAT_LABEL.assigned,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.consumed,
            cell: (sku) => (
              <span className="text-fg tabular-nums">{formatSeats(sku.consumed)}</span>
            ),
          },
          {
            id: "unused",
            header: SEAT_LABEL.unused,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.unused,
            cell: (sku) => (
              <span className="text-warning-fg font-medium tabular-nums">
                {formatSeats(sku.unused)}
              </span>
            ),
          },
          {
            id: "over_assigned",
            header: SEAT_LABEL.overAssigned,
            numeric: true,
            sortable: true,
            accessor: (sku) => sku.over_assigned,
            cell: (sku) =>
              sku.over_assigned > 0 ? (
                <span className="text-danger-fg font-medium tabular-nums">
                  {formatSeats(sku.over_assigned)}
                </span>
              ) : (
                <span className="text-fg-subtle tabular-nums">0</span>
              ),
          },
          {
            id: "utilisation",
            header: "Utilisation",
            width: 180,
            sortable: true,
            accessor: (sku) => sku.utilisation_pct ?? 0,
            cell: (sku) => {
              const tone = utilisationTone(sku.utilisation_pct);
              return (
                <Meter.Root
                  value={Math.min(sku.utilisation_pct ?? 0, 100)}
                  min={0}
                  max={100}
                  size="sm"
                  tone={tone === "neutral" ? "accent" : tone}
                  aria-valuetext={`${formatPct(sku.utilisation_pct)} of seats assigned`}
                >
                  <div className="mb-1 flex justify-between">
                    <Meter.Label className="text-fg-muted text-xs">
                      {formatPct(sku.utilisation_pct)}
                    </Meter.Label>
                  </div>
                  <Meter.Track>
                    <Meter.Indicator />
                  </Meter.Track>
                </Meter.Root>
              );
            },
          },
          ...(pricesConfigured
            ? [
                {
                  id: "cost",
                  header: "Est. monthly waste",
                  numeric: true,
                  sortable: true,
                  accessor: (sku: LicenseSkuRow) => sku.estimated_monthly_waste ?? 0,
                  cell: (sku: LicenseSkuRow) => {
                    const money = formatMoney(sku.estimated_monthly_waste, currency);
                    return money ? (
                      <span className="text-danger-fg tabular-nums">{money}</span>
                    ) : (
                      <span className="text-fg-subtle text-xs">No rate set</span>
                    );
                  },
                },
              ]
            : []),
          {
            id: "flags",
            header: "",
            width: 150,
            cell: (sku) => (
              <div className="flex flex-wrap gap-1">
                {sku.over_assigned > 0 && (
                  <Badge tone="danger" variant="subtle" size="xs">
                    {sku.grace > 0 ? "Ends with grace" : "Unbacked"}
                  </Badge>
                )}
                {!sku.counted && (
                  <Badge tone="neutral" variant="subtle" size="xs">
                    {(sku.excluded_reason && EXCLUSION_LABEL[sku.excluded_reason]) || "Excluded"}
                  </Badge>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
